import CalendarFilters from './calendar-filters';
import { useCalendarFiltersContext } from '@calendar/context/CalendarFiltersContext';
import { ViewFeatured, ViewList, ViewGrid, ViewNone } from './events';
import type { Event, Category, View } from '@/types/calendar';
import { parseDateString, parseDateTimeString } from '@calendar/lib/utils';

interface CalendarContainerProps {
  events: Event[]
  isLoading?: boolean
  error?: Error | null
  categories: Category[]
  categoriesLoading?: boolean
  categoriesError?: Error | null
  views: View[]
}

export function CalendarContainer({ events, isLoading, error, categories, categoriesLoading, categoriesError, views }: CalendarContainerProps) {
  const { filters, setFilters } = useCalendarFiltersContext();

  // Show loading state
  if (isLoading || categoriesLoading) {
    return (
      <div className="app--calendar__container empty">
        <div className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:align-[-0.125em] tw:motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
        <p className="tw:mt-4 tw:text-muted-foreground">Loading events...</p>
      </div>
    );
  }

  // Show error state
  if (error || categoriesError) {
    return (
      <div className="app--calendar__container error">
        <p>Error loading events: {error?.message || categoriesError?.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="tw:mt-4 tw:px-4 tw:py-2 tw:bg-red-100 tw:text-red-700 tw:rounded-md hover:tw:bg-red-200"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Filter events based on current filters
  // Set today to midnight for proper date comparison (events at 00:00 should not be filtered out)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const filteredEvents = events.filter((event) => {
    if(!event.date) {
      return false;
    }

    // Parse date in local timezone (not UTC) to avoid timezone conversion issues
    const eventDate = parseDateString(event.date);

    // Filter by season
    const eventSeason = event.season || '2024-25';
    if (filters.season && eventSeason !== filters.season) {
      return false;
    }

    // Filter by month
    const eventMonth = eventDate.toLocaleString('en-US', { month: 'short' }).toLowerCase();
    if (filters.month && eventMonth !== filters.month) {
      return false;
    }

    // Filter by category
    if (filters.category && !event.categories.includes(filters.category)) {
      return false;
    }

    // Filter by upcoming
    if (filters.upcomingOnly && eventDate < today) {
      return false;
    }

    // Filter by free/ticketed
    if (filters.freeOnly && !event.isFree) {
      return false;
    }

    if (filters.ticketedOnly && !event.isTicketed) {
      return false;
    }

    // If both free and ticketed are checked, show all events
    if (filters.freeOnly && filters.ticketedOnly) {
      return true;
    }

    // If neither free nor ticketed are checked, show all events
    if (!filters.freeOnly && !filters.ticketedOnly) {
      return true;
    }

    return true;
  });

  // Sort events based on selected sort option
  const sortedEvents = [ ...filteredEvents ].sort((a, b) => {
    switch (filters.sortBy) {
      case 'date-asc': {
        // Try datetime first (includes time for proper chronological sorting)
        const aDateTime = parseDateTimeString(a.datetime);
        const bDateTime = parseDateTimeString(b.datetime);

        // If both have valid datetime, use it
        if (aDateTime && bDateTime) {
          return aDateTime.getTime() - bDateTime.getTime();
        }

        // Fall back to date-only sorting for backward compatibility
        return parseDateString(a.date).getTime() - parseDateString(b.date).getTime();
      }
      case 'date-desc': {
        // Try datetime first (includes time for proper chronological sorting)
        const aDateTime = parseDateTimeString(a.datetime);
        const bDateTime = parseDateTimeString(b.datetime);

        // If both have valid datetime, use it
        if (aDateTime && bDateTime) {
          return bDateTime.getTime() - aDateTime.getTime();
        }

        // Fall back to date-only sorting for backward compatibility
        return parseDateString(b.date).getTime() - parseDateString(a.date).getTime();
      }
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      default:
        return 0;
    }
  });

  return (
    <div className="app--calendar__container">
      <div className="app--calendar__filters">
        <CalendarFilters setFilters={setFilters} categories={categories} filters={filters} views={views} count={sortedEvents.length} />
      </div>

      {/* Content sections */}
      <div className="app--calendar__events" data-view={filters.view} data-count={sortedEvents.length}>
        {!sortedEvents.length && <ViewNone setFilters={setFilters} />}
        {sortedEvents.length > 0 && filters.view === 'featured' && <ViewFeatured events={sortedEvents} categories={categories} />}
        {sortedEvents.length > 0 && filters.view === 'list' && <ViewList events={sortedEvents} categories={categories} /> }
        {sortedEvents.length > 0 && filters.view === 'grid' && <ViewGrid events={sortedEvents} categories={categories} filters={filters} />}
      </div>
    </div>
  );
}
